import React from 'react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import AccordionGroup from '@mui/joy/AccordionGroup';
import Accordion from '@mui/joy/Accordion';
import AccordionDetails from '@mui/joy/AccordionDetails';
import AccordionSummary from '@mui/joy/AccordionSummary';
import TripSpotItem from './TripSpotItem';

// Render a list of spot reviews grouped by visit day. Used by both the
// trip-detail page (saved reviews) and the create-trip preview (drafts).
//
// Accepts items with either server-shape (visit_start_time / visit_from seconds)
// or draft-shape (visitStartTime). Items with no visit time fall into a trailing
// "Unscheduled" bucket.
const getStartMs = (r) => {
    if (r.visit_start_time) return new Date(r.visit_start_time).getTime();
    if (r.visitStartTime) return new Date(r.visitStartTime).getTime();
    if (r.visit_from) return r.visit_from * 1000;
    return null;
};

const dayKey = (ms) => {
    const d = new Date(ms);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatDay = (ms) =>
    new Date(ms).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });

const SpotReviewDayGroups = ({ spotReviews, isDraft, fromPath, readOnly, onDetach }) => {
    if (!spotReviews || spotReviews.length === 0) return null;

    const sorted = [...spotReviews].sort((a, b) => {
        const tA = getStartMs(a) ?? Infinity;
        const tB = getStartMs(b) ?? Infinity;
        return tA - tB;
    });

    const groupsMap = new Map();
    for (const r of sorted) {
        const ms = getStartMs(r);
        const key = ms != null ? dayKey(ms) : 'unscheduled';
        if (!groupsMap.has(key)) groupsMap.set(key, { ms, items: [] });
        groupsMap.get(key).items.push(r);
    }
    const groups = Array.from(groupsMap.entries()).map(([key, v]) => ({ key, ms: v.ms, items: v.items }));
    let dayIdx = 0;

    return (
        <AccordionGroup disableDivider variant="plain" transition="0.2s">
            {groups.map((g) => {
                const isScheduled = g.key !== 'unscheduled';
                const label = isScheduled
                    ? `Day ${++dayIdx} · ${formatDay(g.ms)}`
                    : 'Unscheduled';
                return (
                    <Accordion key={g.key} defaultExpanded={true}>
                        <AccordionSummary>
                            <Typography level="title-md">{label}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                                {g.items.map((item, idx) => {
                                    // Draft items don't have stable ids; use the
                                    // index in the original draftSpotReviews array
                                    // so delete works correctly.
                                    const draftIndex = isDraft
                                        ? spotReviews.indexOf(item)
                                        : undefined;
                                    const key = isDraft ? `draft-${draftIndex}` : item.id;
                                    return (
                                        <TripSpotItem
                                            key={key}
                                            spotReview={item}
                                            fromPath={fromPath}
                                            isDraft={isDraft}
                                            draftIndex={draftIndex}
                                            readOnly={readOnly}
                                            onDetach={!readOnly && !isDraft ? onDetach : undefined}
                                        />
                                    );
                                })}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </AccordionGroup>
    );
};

export default SpotReviewDayGroups;
